import axios from 'axios';
import { API_EXCEPTION_MSG, LINE_TYPE } from '../../Common';
import { responseType } from '../../utils/TableCells';
import { BAD_REQUEST, CommonResult, INTERNAL_SERVER_ERROR, OK } from '../Common';

const endpoint = process.env.REACT_APP_API_URL;
const URL = `${endpoint}/aggregation`;

export interface AggregationApiRequest {
  userId: string;
  agencyCd: string[];
  searchConditionId: number;
  statusType: string;
  aggsType: string;
}

export interface SearchBranch {
  branchGroupId?: number;
  branchConditionName: string;
  specifiedCondition: string[];
}

export interface aggregationItem {
  [key: string]: string | number | SearchBranch;
}

export interface CommodityCategoryContractInfo {
  [key: string]: string;
}

export interface AggregationApiResponse {
  result: CommonResult;
  aggsNumber: Array<aggregationItem>;
  searchBranch: Array<SearchBranch>;
  commodityCategoryContractInfo: CommodityCategoryContractInfo;
}

function getAggsTypeCode(aggsType: string) {
  switch (aggsType) {
    case LINE_TYPE.RESULT:
      return '0072';
    case LINE_TYPE.ACTION:
      return '0073';
    case LINE_TYPE.PROB:
      return '0074';
    case LINE_TYPE.PERIOD:
      return '0075';
    case LINE_TYPE.PENDING:
      return '0080';
    default:
      return '';
  }
}

/**
 * 集約件数取得API
 * @param request 集約件数取得APIのリクエスト
 */
export async function aggregationApi(request: AggregationApiRequest): Promise<AggregationApiResponse> {
  try {
    //AggsTypeのコード値への変換
    const apiRequest = Object.assign({}, request);
    apiRequest.aggsType = getAggsTypeCode(request.aggsType);

    const headers = {
      'Content-Type': 'application/json',
    };
    const response = await axios.post<AggregationApiResponse>(URL, apiRequest, { headers });
    const data = response.data;
    const status = response.status;

    switch (status) {
      case OK.status:
        return data;
      case BAD_REQUEST.status:
        return data;
      case INTERNAL_SERVER_ERROR.status:
        throw new Error(data.result.resultCd);
      default:
        throw new Error('9999');
    }
  } catch (error) {
    const errCode = error.message;
    return {
      aggsNumber: [],
      searchBranch: [],
      commodityCategoryContractInfo: {},
      result: {
        jobDateTime: '',
        resultCd: '9999',
        resultMessageDtoList: [
          {
            resultMessageCd: '9999',
            resultMessage: `${API_EXCEPTION_MSG} エラーコード:${errCode}`,
          },
        ],
      },
    };
  }
}

/**
 * 集約件数取得APIから返却されたデータを、商談件数表示画面の表示テーブル用に構造を変換する
 * @param aggregationItems 集約件数取得APIから返却されたデータ
 */
export function convertResponseToTableCells(aggregationItems: aggregationItem[]): responseType {
  const tableCellsArgs: responseType = {};
  aggregationItems.forEach((item) => {
    if (typeof item['aggsCodeNm'] === 'string') {
      const itemName: string = item['aggsCodeNm'];

      const aggregationNumbers: { [key: string]: number } = {};
      Object.keys(item)
        .filter((key) => {
          return key !== 'aggsCodeNm' && key !== 'aggsSearchBranch';
        })
        .forEach((key) => {
          const num = item[key];
          if (typeof num === 'number') {
            aggregationNumbers[key] = num;
          }
        });
      tableCellsArgs[itemName] = aggregationNumbers;
    }
  });

  return tableCellsArgs;
}

/**
 * 集約件数取得APIから返却されたデータを、検索枝条件に構造を変換する
 * @param aggregationItems 集約件数取得APIから返却されたデータ
 */
export function convertResponseToSearchBranch(aggregationItems: aggregationItem[]): { [key: string]: SearchBranch } {
  const searchBranchItem: { [key: string]: SearchBranch } = {};
  aggregationItems.forEach((item) => {
    if (typeof item['aggsCodeNm'] === 'string') {
      const itemName: string = item.aggsCodeNm;
      const aggsSearchBranch = item.aggsSearchBranch;
      if (typeof aggsSearchBranch !== 'string' && typeof aggsSearchBranch !== 'number') {
        searchBranchItem[itemName] = aggsSearchBranch;
      }
    }
  });

  return searchBranchItem;
}
